/**
 * **撤销 / 重做** —— 从 Playground.vue 抽出来。
 *
 * ## 这个文件负责什么
 *
 * 快照式历史（为什么不用命令式逆操作，见 `graph/history.ts` 的注释）。
 * 状态（`history` / `historyTick` / 合并窗口）跟着一起搬进来了 ——
 * 外面只有 `commit` / `doUndo` / `doRedo` / `historyState` 四个口子。
 *
 * `historyTick` 是计数器：历史是纯数据、不带响应式，
 * 界面（菜单项可用性、工具栏按钮）靠它触发重算。
 *
 * ## ⚠️ 抽出来的方式：函数体逐字不动
 *
 * 宿主的作用域状态（`nodes` / `edges` / `flowStore` / `showNotice`）
 * 由 `ctx` 注入 —— 搬的时候**一行逻辑都没改**。
 */
import { computed, nextTick, ref } from 'vue'
/* ⚠️ 结构类型，不是引擎的类型 —— 换引擎时这一行不用改（`flowTypes.ts`） */
import type { FlowEdge as Edge, FlowNode as Node } from '@/graph/flowTypes'
import { createHistory } from '@/graph/history'
import { snapshotGraph } from '@/graph/graphSnapshot'
import type { GraphSnapshot } from '@/graph/graphSnapshot'
import type { FlowStoreApi } from '@/graph/flowStore'

export interface UndoRedoCtx {
  nodes: { value: Node[] }
  edges: { value: Edge[] }
  flowStore: { value: FlowStoreApi | null }
  showNotice: (message: string, actions?: NoticeActionInput) => void
}

/**
 * 提示条上的动作 —— 只描述这个 composable 用得到的形状。
 *
 * ⚠️ 本地定义，不从 Playground 导 —— composable 不该反向依赖宿主文件。
 * 结构上与 Playground 里的 `NoticeAction` 相容，传进来不会有类型问题。
 */
export interface NoticeActionInput {
  label: string
  run: () => void
}

export function useUndoRedo(ctx: UndoRedoCtx) {
  const { nodes, edges, flowStore, showNotice } = ctx

/**
 * 快照式历史（为什么不用命令式逆操作，见 graph/history.ts 的注释）。
 *
 * `historyTick` 是计数器：历史是纯数据、不带响应式，
 * 界面（菜单项可用性、工具栏按钮）靠它触发重算。
 */
const history = createHistory<GraphSnapshot>({ limit: 60 })
const historyTick = ref(0)

/** 连续同类操作合并成一步的窗口起点 */
let lastCommitLabel = ''
let lastCommitAt = 0

/**
 * 在改动**之前**记一份现状。
 *
 * `coalesceMs > 0` 时，同一标签的连续操作在窗口内只记第一次 ——
 * 否则拖一次宽度会塞进几十步历史。
 */
function commit(label: string, coalesceMs = 0) {
  const now = Date.now()
  if (coalesceMs > 0 && label === lastCommitLabel && now - lastCommitAt < coalesceMs) {
    lastCommitAt = now
    return
  }
  history.record(label, currentSnapshot())
  historyTick.value += 1
  lastCommitLabel = label
  lastCommitAt = now
}

function currentSnapshot(): GraphSnapshot {
  return snapshotGraph(nodes.value, edges.value)
}

function applySnapshot(snap: GraphSnapshot) {
  nodes.value = snap.nodes as unknown as Node[]
  edges.value = snap.edges as unknown as Edge[]
  nextTick(() => flowStore.value?.updateNodeInternals())
}

function doUndo() {
  const entry = history.undo(currentSnapshot())
  if (!entry) {
    showNotice('没有可撤销的操作')
    return
  }
  applySnapshot(entry.state)
  historyTick.value += 1
  showNotice(`已撤销：${entry.label}`)
}

function doRedo() {
  const entry = history.redo(currentSnapshot())
  if (!entry) {
    showNotice('没有可重做的操作')
    return
  }
  applySnapshot(entry.state)
  historyTick.value += 1
  showNotice(`已重做：${entry.label}`)
}

/**
 * **清空历史**（换文档 / 打开文件时必须调）。
 *
 * ⚠️ 这不是洁癖，是**数据完整性**：历史里存的是**旧那份文档**的快照，
 * 换了文档之后按 Ctrl+Z 会把旧文档的整张图**装进新文档里** ——
 * 用户看到的是"撤销一下，我别的文档的内容跑进来了"，
 * 而那一刻新文档已经被改坏了（再存一次就落盘）。
 *
 * 打开文件那条路原来就有这个洞（`useTemplateFile` 的注释写着
 * "没有撤销入口" —— 那是对**被替换掉的那份图**说的），有了文档库之后
 * "换一份文档"变成一等操作，这个洞必须先补上。
 */
function clearHistory() {
  history.clear()
  historyTick.value += 1
  /* 下一次 commit 不该和上一条合并（标签相同也当新的一步） */
  lastCommitLabel = ''
  lastCommitAt = 0
}

/** 顶栏按钮与菜单项都看它 */
const historyState = computed(() => {
  void historyTick.value
  return {
    canUndo: history.canUndo(),
    canRedo: history.canRedo(),
    ...history.peek(),
  }
})

  return { commit, doUndo, doRedo, clearHistory, historyState }
}
